import axios from 'axios';
import { getCookie } from '@/utils/cookies/functions';
import { handleError } from '@/frameworks/error';
import { ENDPOINTS } from '@/utils/endpoints/constants';

/**
 * Single axios instance for all aurora-ms API calls.
 *
 * - Base URL is set once here — change it in one place if the API moves.
 * - Bearer token is attached automatically from the cookie on every request.
 * - All error responses are normalised through handleError so callers
 *   only need to catch a consistent { status, message, type } shape.
 */
const apiClient = axios.create({
  baseURL: ENDPOINTS.AURORA,
  headers: { accept: '*/*' },
});

// Attach token if present (public endpoints like /authenticate work fine
// without it — the cookie simply won't exist at that point).
apiClient.interceptors.request.use(config => {
  const token = getCookie('Bearer');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Normalise all error responses through the existing handleError helper.
// Retry once on server/network errors (handles Lambda cold start delays).
apiClient.interceptors.response.use(
  response => response,
  async error => {
    const { config, response } = error;
    if (!config._retry && (!response || response.status >= 500)) {
      config._retry = true;
      await new Promise(r => setTimeout(r, 3000));
      return apiClient(config);
    }
    handleError(error);
  }
);

export default apiClient;
// test
